import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { ladditionAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Loader2, Utensils, Trophy, Layers, ChevronDown, ChevronRight, Columns3, FileUp } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import WeekScroller from '@/components/sales/WeekScroller';

const isVAE = (cat) => /^VAE|EMPORT/i.test((cat || '').trim());
const itemIsVAE = (it) => it.type ? it.type === 'vae' : isVAE(it.category);

const DAY_SHORT = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

function CategorySections({ items, caTtc, dayCols, platformLines, payments }) {
    const [openSections, setOpenSections] = useState({ vae: true, salle: true });
    const [openCats, setOpenCats] = useState({});

    const sections = [
        { key: 'vae', label: '🥡 EMPORTER', bg: '#047857', items: items.filter(itemIsVAE) },
        { key: 'salle', label: '🍽️ SUR PLACE', bg: '#8b5cf6', items: items.filter(it => !itemIsVAE(it)) },
    ];
    const platformSections = ['Dood', 'Easy']
        .map(mode => ({
            key: `pf-${mode.toLowerCase()}`,
            label: mode === 'Dood' ? '🌐 DONT DOOD — commandes internet (emporter)' : '🛵 DONT EASY — livraison (emporter)',
            bg: mode === 'Dood' ? '#16a34a' : '#65a30d',
            items: (platformLines || []).filter(l => l.mode === mode).map(l => ({ ...l, ht: null })),
        }))
        .filter(s => s.items.length > 0);

    const sum = (arr, k) => arr.reduce((s, i) => s + (i[k] || 0), 0);
    const daySum = (arr, iso, k) => arr.reduce((s, i) => s + (i.by_day?.[iso]?.[k] || 0), 0);
    const nCols = dayCols ? dayCols.length + 3 : 4;

    const dayCells = (arr, cls = '') => dayCols.map(d => {
        const q = daySum(arr, d.iso, 'qty');
        return <td key={d.iso} className={`px-2 py-1 text-right tabular-nums ${cls}`}>{q ? Math.round(q * 100) / 100 : <span className="opacity-30">—</span>}</td>;
    });

    const tVae = sum(sections[0].items, 'ttc');
    const tSalle = sum(sections[1].items, 'ttc');
    const ecart = Math.round(((caTtc || 0) - tVae - tSalle) * 100) / 100;

    const allSections = [sections[0], ...platformSections, sections[1]];
    return (
        <>
            {allSections.map(sec => {
                const open = openSections[sec.key];
                const cats = Object.entries(sec.items.reduce((acc, it) => {
                    const k = it.category || 'Sans catégorie';
                    (acc[k] = acc[k] || []).push(it);
                    return acc;
                }, {})).sort((a, b) => sum(b[1], 'ttc') - sum(a[1], 'ttc'));
                return (
                    <React.Fragment key={sec.key}>
                        <tr className="text-white cursor-pointer select-none" style={{ backgroundColor: sec.bg }} onClick={() => setOpenSections(p => ({ ...p, [sec.key]: !p[sec.key] }))} data-testid={`laddition-section-${sec.key}`}>
                            <td className="px-3 py-2 font-bold">
                                {open ? <ChevronDown className="w-4 h-4 inline mr-1 -mt-0.5" /> : <ChevronRight className="w-4 h-4 inline mr-1 -mt-0.5" />}
                                {sec.label}
                            </td>
                            {dayCols ? dayCells(sec.items, 'font-semibold') : (
                                <td className="px-3 py-2 text-right font-semibold">{Math.round(sum(sec.items, 'qty'))}</td>
                            )}
                            <td className="px-3 py-2 text-right font-bold">{formatCurrency(sum(sec.items, 'ttc'))}</td>
                            {!dayCols && <td className="px-3 py-2 text-right">{formatCurrency(sum(sec.items, 'ht'))}</td>}
                            {dayCols && <td className="px-3 py-2 text-right font-semibold">{Math.round(sum(sec.items, 'qty'))}</td>}
                        </tr>
                        {open && cats.map(([cat, catItems]) => {
                            const ck = `${sec.key}:${cat}`;
                            const cOpen = !!openCats[ck];
                            return (
                                <React.Fragment key={ck}>
                                    <tr className="cursor-pointer select-none hover:brightness-95" style={{ backgroundColor: '#e9eef5' }} onClick={() => setOpenCats(p => ({ ...p, [ck]: !p[ck] }))} data-testid={`laddition-cat-${cat}`}>
                                        <td className="px-3 py-1.5 pl-6 font-bold text-slate-700">
                                            {cOpen ? <ChevronDown className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-slate-400" />}
                                            {cat}
                                            <span className="ml-1.5 text-[10px] font-normal text-slate-400">({catItems.length})</span>
                                        </td>
                                        {dayCols ? dayCells(catItems, 'font-semibold text-slate-700') : (
                                            <td className="px-3 py-1.5 text-right font-semibold">{Math.round(sum(catItems, 'qty'))}</td>
                                        )}
                                        <td className="px-3 py-1.5 text-right font-bold">{formatCurrency(sum(catItems, 'ttc'))}</td>
                                        {!dayCols && <td className="px-3 py-1.5 text-right text-slate-500 font-semibold">{formatCurrency(sum(catItems, 'ht'))}</td>}
                                        {dayCols && <td className="px-3 py-1.5 text-right font-semibold">{Math.round(sum(catItems, 'qty'))}</td>}
                                    </tr>
                                    {cOpen && [...catItems].sort((a, b) => (b.qty || 0) - (a.qty || 0)).map((it, idx) => (
                                        <tr key={idx} className="table-row">
                                            <td className="px-3 py-1 pl-12 font-medium">{it.product}</td>
                                            {dayCols ? dayCols.map(d => {
                                                const q = it.by_day?.[d.iso]?.qty || 0;
                                                return <td key={d.iso} className="px-2 py-1 text-right tabular-nums">{q ? Math.round(q * 100) / 100 : <span className="text-slate-200">—</span>}</td>;
                                            }) : (
                                                <td className="px-3 py-1 text-right">{it.qty}</td>
                                            )}
                                            <td className="px-3 py-1 text-right">{formatCurrency(it.ttc)}</td>
                                            {!dayCols && <td className="px-3 py-1 text-right text-slate-500">{formatCurrency(it.ht)}</td>}
                                            {dayCols && <td className="px-3 py-1 text-right tabular-nums">{it.qty}</td>}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </React.Fragment>
                );
            })}
            <tr className={ecart === 0 ? 'bg-slate-50' : 'bg-amber-50'} data-testid="laddition-coherence-row">
                <td colSpan={nCols - 2} className={`px-3 py-2 text-xs font-semibold ${ecart === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {ecart === 0
                        ? '✓ Cohérent : Emporter + Sur place = CA TTC (aucun doublon)'
                        : `⚠️ Écart de ${formatCurrency(ecart)} entre (Emporter + Sur place) et le CA TTC déclaré`}
                </td>
                <td className="px-3 py-2 text-right font-bold">{formatCurrency(tVae + tSalle)}</td>
                <td className="px-3 py-2 text-right text-xs text-slate-500">CA TTC : {formatCurrency(caTtc)}</td>
            </tr>
        </>
    );
}

function mondayOfIso(dateIso) {
    const d = new Date(dateIso + 'T00:00:00');
    const dow = d.getDay();
    d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
    return d.toISOString().slice(0, 10);
}

const Kpi = ({ label, value, accent }) => (
    <div className="rounded-lg border bg-white px-3 py-2">
        <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
        <p className={`text-lg font-bold ${accent || 'text-slate-900'}`}>{value ?? '—'}</p>
    </div>
);

export default function LadditionTab() {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [importing, setImporting] = useState(false);
    const fileInputRef = React.useRef(null);
    const pdfInputRef = React.useRef(null);
    const [viewMode, setViewMode] = useState('top'); // 'top' | 'category'
    const [catMode, setCatMode] = useState('cumul'); // 'cumul' | 'days'
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [periodDay, setPeriodDay] = useState(null); // vue jour dans la sélection
    const [selected, setSelected] = useState(null); // détail affiché (jour ou agrégat période)

    const coverageDates = useMemo(() => reports.map(r => r.report_date), [reports]);
    const minDate = reports.length ? reports[reports.length - 1].report_date : null;
    const maxDate = reports.length ? reports[0].report_date : null;

    // 7 boutons jour quand la sélection est exactement une semaine
    const weekDays = useMemo(() => {
        if (!startDate || !endDate) return null;
        const s = new Date(startDate + 'T00:00:00');
        const e = new Date(endDate + 'T00:00:00');
        if (e - s !== 6 * 86400000) return null;
        const labels = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
        const out = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(s);
            d.setDate(d.getDate() + i);
            out.push({ iso: d.toISOString().slice(0, 10), label: labels[d.getDay()] });
        }
        return out;
    }, [startDate, endDate]);

    const load = useCallback(async () => {
        try {
            const res = await ladditionAPI.reports({ limit: 400 });
            setReports(res.data);
            if (res.data.length > 0 && !startDate) {
                const mon = mondayOfIso(res.data[0].report_date);
                const sun = new Date(mon + 'T00:00:00');
                sun.setDate(sun.getDate() + 6);
                setStartDate(mon);
                setEndDate(sun.toISOString().slice(0, 10));
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de chargement');
        } finally { setLoading(false); }
        // eslint-disable-next-line
    }, []);

    useEffect(() => { load(); }, [load]);

    // Charge le détail : jour précis ou agrégat de la période
    useEffect(() => {
        if (!startDate || !endDate) return;
        let cancelled = false;
        (async () => {
            try {
                if (periodDay) {
                    const rep = reports.find(r => r.report_date === periodDay);
                    if (!rep) { setSelected(null); return; }
                    const d = await ladditionAPI.reportDetail(rep.id);
                    if (!cancelled) setSelected({ ...d.data, _isPeriod: false });
                } else {
                    const d = await ladditionAPI.summary({ start_date: startDate, end_date: endDate });
                    if (!cancelled) setSelected(d.data.nb_days ? { ...d.data, _isPeriod: true } : null);
                }
            } catch { toast.error('Erreur de chargement'); }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, [startDate, endDate, periodDay, reports]);

    const sync = async () => {
        setSyncing(true);
        try {
            const res = await ladditionAPI.sync(7);
            toast.success(`${res.data.new_reports} nouveau(x) rapport(s) importé(s) (${res.data.emails_seen} email(s) vus)`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de synchronisation');
        } finally { setSyncing(false); }
    };

    const importFiles = async (files) => {
        if (!files.length) return;
        setImporting(true);
        let created = 0, skipped = 0, errors = 0;
        for (const f of files) {
            try {
                const fd = new FormData();
                fd.append('file', f);
                const res = await ladditionAPI.importExcel(fd);
                created += res.data.created.length;
                skipped += res.data.skipped.length;
            } catch (e) {
                errors += 1;
                toast.error(`${f.name} : ${e.response?.data?.detail || 'erreur'}`);
            }
        }
        if (created) toast.success(`${created} jour(s) importé(s)${skipped ? `, ${skipped} déjà présent(s)` : ''}`);
        else if (!errors) toast.info(`Aucun nouveau jour (${skipped} déjà présent(s))`);
        setImporting(false);
        await load();
    };

    const importPdfFiles = async (files) => {
        if (!files.length) return;
        setImporting(true);
        let created = [], skipped = 0, errors = 0;
        for (const f of files) {
            try {
                const fd = new FormData();
                fd.append('file', f);
                const res = await ladditionAPI.importZPdf(fd);
                created = created.concat(res.data.created);
                skipped += res.data.skipped.length;
            } catch (e) {
                errors += 1;
                toast.error(`${f.name} : ${e.response?.data?.detail || 'erreur'}`);
            }
        }
        if (created.length) toast.success(`Z importé(s) : ${created.join(', ')}${skipped ? ` — ${skipped} déjà présent(s)` : ''}`);
        else if (!errors) toast.info(`Aucun nouveau Z (${skipped} déjà présent(s))`);
        setImporting(false);
        await load();
    };

    if (loading) return <div className="py-12 text-center text-slate-400">Chargement…</div>;

    const periodLabel = selected?._isPeriod
        ? `Du ${startDate} au ${endDate} (${selected.nb_days} jour${selected.nb_days > 1 ? 's' : ''})`
        : selected?.report_date;

    return (
        <div className="space-y-4" data-testid="laddition-tab">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Utensils className="w-5 h-5 text-rose-500" />
                    <span className="font-semibold">Caisse L'Addition — Z quotidiens</span>
                    {reports[0] && <Badge variant="outline" className="text-xs">Dernier : {reports[0].report_date} ({reports[0].site_label})</Badge>}
                </div>
                <div className="flex items-center gap-2">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx"
                        multiple
                        className="hidden"
                        onChange={e => { importFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
                        data-testid="laddition-import-input"
                    />
                    <input
                        ref={pdfInputRef}
                        type="file"
                        accept=".pdf"
                        multiple
                        className="hidden"
                        onChange={e => { importPdfFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
                        data-testid="laddition-import-pdf-input"
                    />
                    <Button variant="outline" size="sm" onClick={() => pdfInputRef.current?.click()} disabled={importing} data-testid="laddition-import-pdf-btn">
                        {importing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileUp className="w-4 h-4 mr-1.5" />}
                        Importer Z (PDF)
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={importing} data-testid="laddition-import-btn">
                        {importing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileUp className="w-4 h-4 mr-1.5" />}
                        Importer Excel
                    </Button>
                    <Button variant="outline" size="sm" onClick={sync} disabled={syncing} data-testid="laddition-sync-btn">
                        {syncing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                        Synchroniser
                    </Button>
                </div>
            </div>

            {reports.length === 0 ? (
                <Card><CardContent className="py-10 text-center text-slate-400">
                    Aucun rapport Z importé pour le moment. Cliquez sur « Synchroniser ».
                </CardContent></Card>
            ) : (
                <>
                    <Card>
                        <CardContent className="pt-3 pb-3 space-y-2">
                            <WeekScroller
                                coverageDates={coverageDates}
                                minDate={minDate}
                                maxDate={maxDate}
                                startDate={startDate}
                                endDate={endDate}
                                onChange={(s, e) => { setStartDate(s); setEndDate(e); setPeriodDay(null); }}
                            />
                            {weekDays && (
                                <div className="flex flex-wrap items-center justify-center gap-1.5" data-testid="laddition-week-days">
                                    <span className="text-[10px] text-slate-400 font-semibold uppercase mr-0.5">Voir un jour :</span>
                                    {weekDays.map(d => {
                                        const hasData = coverageDates.includes(d.iso);
                                        const isActive = periodDay === d.iso;
                                        return (
                                            <button
                                                key={d.iso}
                                                type="button"
                                                onClick={() => {
                                                    if (!hasData) return;
                                                    setPeriodDay(isActive ? null : d.iso);
                                                }}
                                                disabled={!hasData}
                                                title={hasData ? (isActive ? 'Revenir à la vue semaine' : `Vue jour du ${d.iso}`) : `${d.iso} — pas de rapport Z`}
                                                className={`shrink-0 px-4 py-0.5 rounded-md border text-center leading-tight ${isActive ? 'bg-slate-900 text-white border-slate-900' : hasData ? 'bg-white text-slate-700 border-slate-200 hover:border-slate-500 hover:bg-slate-50' : 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed'}`}
                                                data-testid={`laddition-day-shortcut-${d.iso}`}
                                            >
                                                <div className="text-[10px] font-bold">{d.label}</div>
                                                <div className={`text-[8px] ${isActive ? 'text-slate-300' : 'text-slate-400'}`}>{d.iso.slice(8)}/{d.iso.slice(5, 7)}</div>
                                            </button>
                                        );
                                    })}
                                    {periodDay && (
                                        <button
                                            type="button"
                                            onClick={() => setPeriodDay(null)}
                                            className="ml-1 px-2 py-1 rounded-md border text-[10px] font-semibold bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                                            data-testid="laddition-back-to-period"
                                        >✕ Vue semaine</button>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {!selected ? (
                        <Card><CardContent className="py-10 text-center text-slate-400">
                            Aucun rapport Z sur cette période.
                        </CardContent></Card>
                    ) : (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold" data-testid="laddition-selection-label">{selected.site_label} — {periodLabel}</span>
                                {!selected._isPeriod && selected.commentaire && selected.commentaire !== 'Aucun commentaire' && (
                                    <span className="text-xs text-slate-500 italic">« {selected.commentaire} »</span>
                                )}
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                <Kpi label="CA TTC" value={formatCurrency(selected.ca_ttc)} accent="text-rose-600" />
                                <Kpi label="CA HT" value={formatCurrency(selected.ca_ht)} />
                                <Kpi label="Couverts" value={selected.couverts} />
                                <Kpi label="Panier moyen" value={formatCurrency(selected.panier_moyen)} />
                                <Kpi label="Ticket moyen" value={formatCurrency(selected.ticket_moyen)} />
                                <Kpi label="Remises" value={formatCurrency(selected.remises)} />
                                <Kpi label="Corrections cuisine" value={formatCurrency((selected.corrections_avant || 0) + (selected.corrections_apres || 0))} />
                                <Kpi label="Annulations" value={formatCurrency(selected.annulations)} />
                            </div>

                            {selected.payments?.length > 0 && (() => {
                                const platforms = selected.payments.filter(p => /dood|easy/i.test(p.mode));
                                if (!platforms.length) return null;
                                return (
                                    <div className="flex flex-wrap items-center gap-1.5" data-testid="laddition-payments-strip">
                                        <span className="text-[10px] font-semibold uppercase text-slate-400 mr-1">Plateformes :</span>
                                        {platforms.map(p => {
                                            const isDood = /dood/i.test(p.mode);
                                            return (
                                                <span key={p.mode}
                                                    className={`inline-flex items-baseline gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${isDood ? 'bg-green-50 border-green-300 text-green-800' : 'bg-lime-50 border-lime-400 text-lime-800'}`}
                                                    data-testid={`laddition-payment-${p.mode.toLowerCase().replace(/\s+/g, '-')}`}>
                                                    {isDood ? '🌐' : '🛵'} {p.mode}
                                                    <span className="tabular-nums">{formatCurrency(p.amount)}</span>
                                                </span>
                                            );
                                        })}
                                        {selected._isPeriod && selected.payments_days < selected.nb_days && (
                                            <span className="text-[10px] text-amber-600 italic">
                                                (ventilation disponible sur {selected.payments_days}/{selected.nb_days} jours)
                                            </span>
                                        )}
                                    </div>
                                );
                            })()}

                            {selected._isPeriod && selected.days?.length > 1 && (
                                <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="laddition-days-strip">
                                    {selected.days.map(d => (
                                        <button
                                            key={d.id}
                                            type="button"
                                            onClick={() => setPeriodDay(d.report_date)}
                                            title={`Voir le détail du ${d.report_date}`}
                                            className="shrink-0 px-2.5 py-1 rounded-md border bg-white border-slate-200 hover:border-rose-400 hover:bg-rose-50 text-center transition-colors"
                                            data-testid={`laddition-period-day-${d.report_date}`}
                                        >
                                            <div className="text-[10px] font-bold text-slate-700">
                                                {DAY_SHORT[new Date(d.report_date + 'T00:00:00').getDay()]} {d.report_date.slice(8)}/{d.report_date.slice(5, 7)}
                                            </div>
                                            <div className="text-[10px] text-slate-500 tabular-nums">{formatCurrency(d.ca_ttc)}</div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            <Card>
                                <CardContent className="p-0">
                                    <div className="flex items-center gap-1 px-3 py-2 border-b bg-slate-50/60">
                                        <button
                                            onClick={() => setViewMode('top')}
                                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${viewMode === 'top' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'}`}
                                            data-testid="laddition-view-top-btn"
                                        >
                                            <Trophy className="w-3.5 h-3.5 inline mr-1" />Top ventes
                                        </button>
                                        <button
                                            onClick={() => setViewMode('category')}
                                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${viewMode === 'category' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'}`}
                                            data-testid="laddition-view-category-btn"
                                        >
                                            <Layers className="w-3.5 h-3.5 inline mr-1" />Par catégorie
                                        </button>
                                        {viewMode === 'category' && selected._isPeriod && weekDays && (
                                            <div className="ml-auto flex items-center gap-1">
                                                <button
                                                    onClick={() => setCatMode('cumul')}
                                                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${catMode === 'cumul' ? 'bg-rose-600 text-white' : 'text-slate-500 hover:text-slate-800 border border-slate-200'}`}
                                                    data-testid="laddition-catmode-cumul"
                                                >Cumul</button>
                                                <button
                                                    onClick={() => setCatMode('days')}
                                                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${catMode === 'days' ? 'bg-rose-600 text-white' : 'text-slate-500 hover:text-slate-800 border border-slate-200'}`}
                                                    data-testid="laddition-catmode-days"
                                                ><Columns3 className="w-3 h-3 inline mr-1" />7 jours</button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="max-h-[520px] overflow-y-auto overflow-x-auto">
                                    {(() => {
                                        const dayCols = viewMode === 'category' && catMode === 'days' && selected._isPeriod && weekDays
                                            ? weekDays.map(d => ({ ...d, short: DAY_SHORT[new Date(d.iso + 'T00:00:00').getDay()] }))
                                            : null;
                                        return (
                                    <table className="w-full data-table text-sm">
                                        <thead><tr className="table-header">
                                            {viewMode === 'top' && <th className="text-left px-3 py-2">Catégorie</th>}
                                            <th className="text-left px-3 py-2">Produit</th>
                                            {dayCols ? (
                                                <>
                                                    {dayCols.map(d => (
                                                        <th key={d.iso} className="text-right px-2 py-2 whitespace-nowrap">
                                                            {d.short}<br /><span className="text-[9px] font-normal text-slate-400">{d.iso.slice(8)}/{d.iso.slice(5, 7)}</span>
                                                        </th>
                                                    ))}
                                                    <th className="text-right px-3 py-2">TTC</th>
                                                    <th className="text-right px-3 py-2">Qté tot.</th>
                                                </>
                                            ) : (
                                                <>
                                                    <th className="text-right px-3 py-2">Qté</th>
                                                    <th className="text-right px-3 py-2">TTC</th>
                                                    <th className="text-right px-3 py-2">HT</th>
                                                </>
                                            )}
                                        </tr></thead>
                                        <tbody>
                                            {viewMode === 'top' ? (
                                                [...(selected.items || [])].sort((a, b) => (b.qty || 0) - (a.qty || 0)).map((it, idx) => (
                                                    <tr key={idx} className="table-row">
                                                        <td className="px-3 py-1 text-xs text-slate-500">{it.category}</td>
                                                        <td className="px-3 py-1 font-medium">{it.product}</td>
                                                        <td className="px-3 py-1 text-right">{it.qty}</td>
                                                        <td className="px-3 py-1 text-right">{formatCurrency(it.ttc)}</td>
                                                        <td className="px-3 py-1 text-right text-slate-500">{formatCurrency(it.ht)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <CategorySections items={selected.items || []} caTtc={selected.ca_ttc} dayCols={dayCols}
                                                    platformLines={selected.platform_lines} payments={selected.payments} />
                                            )}
                                        </tbody>
                                    </table>
                                        );
                                    })()}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
